import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { User } from "@/entities/User";
import { Creator } from "@/entities/Creator";
import { ShoppingBag, Menu, X, Warehouse, LayoutDashboard } from "lucide-react";

export default function Layout({ children, currentPageName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const location = useLocation();

  const refreshCart = () => {
    try {
      const cart = JSON.parse(localStorage.getItem("cw_cart") || "[]");
      setCartCount(cart.reduce((sum, item) => sum + (item.qty || 1), 0));
    } catch {
      setCartCount(0);
    }
  };

  useEffect(() => {
    refreshCart();
    window.addEventListener("cw_cart_updated", refreshCart);
    User.me().then(async u => {
      if (!u) return;
      setIsAdmin(u.role === "admin");
      // Check if user has a creator profile
      const creators = await Creator.filter({ user_id: u.id }, "-createdAt", 1).catch(() => []);
      setIsCreator(creators.length > 0);
    }).catch(() => {});
    return () => window.removeEventListener("cw_cart_updated", refreshCart);
  }, []);

  const navLinks = [
    { label: "Home", page: "Home" },
    { label: "Creators", page: "Creators" },
    { label: "Shop", page: "Shop" },
  ];

  const isActive = (page) => {
    const url = createPageUrl(page);
    return location.pathname === url || (page === "Home" && location.pathname === "/");
  };

  return (
    <div className="min-h-screen bg-[#faf9f7] flex flex-col font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap');
        body { font-family: 'Inter', sans-serif; }
        .font-display { font-family: 'Playfair Display', serif; }
        :root {
          --brand: #c8a96e;
          --brand-dark: #a8893e;
          --ink: #1a1a1a;
          --muted: #6b6b6b;
          --surface: #faf9f7;
          --border: #e8e4de;
        }
      `}</style>

      {/* Top Bar */}
      <div className="bg-[#1a1a1a] text-[#c8a96e] text-xs text-center py-2 tracking-widest uppercase">
        Free shipping on orders over $75 · Handmade with love
      </div>

      {/* Navigation */}
      <header className="bg-white border-b border-[#e8e4de] sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={createPageUrl("Home")} className="flex items-center gap-2 group">
              <div className="w-8 h-8 bg-[#1a1a1a] rounded-sm flex items-center justify-center">
                <Warehouse className="w-4 h-4 text-[#c8a96e]" />
              </div>
              <div>
                <span className="font-display text-lg font-bold text-[#1a1a1a] leading-none block">Creators</span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#c8a96e] font-medium leading-none block">Warehouse</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.page}
                  to={createPageUrl(link.page)}
                  className={`text-sm font-medium tracking-wide transition-colors ${
                    isActive(link.page)
                      ? "text-[#c8a96e] border-b-2 border-[#c8a96e] pb-0.5"
                      : "text-[#1a1a1a] hover:text-[#c8a96e]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {isCreator && !isAdmin && (
                <Link to={createPageUrl("CreatorDashboard")} className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold bg-[#c8a96e]/10 text-[#c8a96e] border border-[#c8a96e]/30 px-3 py-1.5 rounded-full hover:bg-[#c8a96e] hover:text-white transition-colors">
                  <LayoutDashboard className="w-3.5 h-3.5" /> My Dashboard
                </Link>
              )}
              {isAdmin && (
                <Link to={createPageUrl("Admin")} className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold bg-[#c8a96e]/10 text-[#c8a96e] border border-[#c8a96e]/30 px-3 py-1.5 rounded-full hover:bg-[#c8a96e] hover:text-white transition-colors">
                  <LayoutDashboard className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
              <Link to={createPageUrl("Cart")} className="relative p-2 hover:text-[#c8a96e] transition-colors">
                <ShoppingBag className="w-5 h-5 text-[#1a1a1a]" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#c8a96e] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                className="md:hidden p-2"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

          {menuOpen && (
            <div className="md:hidden bg-white border-t border-[#e8e4de] px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.page}
                  to={createPageUrl(link.page)}
                  onClick={() => setMenuOpen(false)}
                  className={`block text-sm font-medium py-2 ${
                    isActive(link.page) ? "text-[#c8a96e]" : "text-[#1a1a1a]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link to={createPageUrl("Admin")} onClick={() => setMenuOpen(false)} className="block text-sm font-medium py-2 text-[#c8a96e]">
                  ⚙️ Admin Dashboard
                </Link>
              )}
              {isCreator && !isAdmin && (
                <Link to={createPageUrl("CreatorDashboard")} onClick={() => setMenuOpen(false)} className="block text-sm font-medium py-2 text-[#c8a96e]">
                  🎨 My Creator Dashboard
                </Link>
              )}
            </div>
          )}
      </header>

      {/* Page Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-[#c8a96e] rounded-sm flex items-center justify-center">
                  <Warehouse className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-display text-lg font-bold">Creators Warehouse</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                A curated marketplace celebrating independent artists, makers, and creators. Every purchase supports a real human making something beautiful.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-widest text-[#c8a96e] mb-4">Explore</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to={createPageUrl("Shop")} className="hover:text-white transition-colors">Shop All</Link></li>
                <li><Link to={createPageUrl("Creators")} className="hover:text-white transition-colors">Meet the Creators</Link></li>
                <li><Link to={createPageUrl("Home")} className="hover:text-white transition-colors">Featured Items</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-widest text-[#c8a96e] mb-4">Community</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><span className="hover:text-white transition-colors cursor-pointer">Become a Creator</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Our Story</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Contact Us</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-10 pt-6 text-center text-xs text-gray-500">
            © {new Date().getFullYear()} Creators Warehouse. All rights reserved. Made with ♥ for makers everywhere.
          </div>
        </div>
      </footer>
    </div>
  );
}